import React, { FC } from "react";
import { HiEllipsisVertical } from "react-icons/hi2";
import { LuBriefcase, LuMail, LuPlus, LuSend } from "react-icons/lu";

import { Card } from "../../../../components/card/Card";
import { SettingsDropdown } from "../../../../components/dropdown/SettingsDropdown";
import { Button } from "../../../../components/newUI/button";
import { Email } from "../../../../factories/emails/models/emails.models";
import { SearchInput } from "../../../emails/components/Search";

type ContactEmailsPageToolbarProps = {
  selectedMail: Email | null;
  onCompose: () => void;
  onAddToExistingWork: () => void;
  onDisconnect: () => void;
};

type EmailConnectionEmptyStateProps = {
  onConnect: () => void;
};

type EmailProviderModalProps = {
  onClose: () => void;
  onConnect: (provider: "google" | "microsoft") => void;
};

export const ContactEmailsPageToolbar: FC<ContactEmailsPageToolbarProps> = ({
  selectedMail,
  onCompose,
  onAddToExistingWork,
  onDisconnect,
}) => (
  <Card extraClasses={"mb-4 overflow-hidden"}>
    <div
      className={
        "flex flex-col gap-3 p-4 font-Manrope xl:flex-row xl:items-center xl:justify-between"
      }>
      <div className={"min-w-[260px] flex-1"}>
        <SearchInput />
      </div>
      <div className={"flex flex-wrap items-center justify-end gap-2"}>
        <Button
          type={"button"}
          variant={"primary"}
          size={"sm"}
          autoWidth
          leftIcon={<LuSend size={15} />}
          className={"h-9 px-3 text-[13px] font-bold"}
          onClick={onCompose}>
          Compose email
        </Button>
        <Button
          type={"button"}
          variant={"outline"}
          size={"sm"}
          autoWidth
          disabled={!selectedMail}
          leftIcon={<LuPlus size={16} />}
          className={"h-9 px-3 text-[13px] font-bold"}
          onClick={onAddToExistingWork}>
          Add email to existing work
        </Button>
        <Button
          type={"button"}
          variant={"outline"}
          size={"sm"}
          autoWidth
          disabled={!selectedMail}
          leftIcon={<LuBriefcase size={15} />}
          className={"h-9 px-3 text-[13px] font-bold"}>
          Create work from email
        </Button>
        <SettingsDropdown
          actionsList={[
            {
              name: "Disconnect",
              action: onDisconnect,
              extraClasses: "text-[#DC2626]",
            },
          ]}>
          <Button
            type={"button"}
            variant={"outline"}
            size={"sm"}
            iconOnly
            autoWidth
            leftIcon={<HiEllipsisVertical size={18} />}
            className={
              "h-9 w-9 shrink-0 rounded-lg border-[#E4E7EC] p-0 text-[#9097A8] hover:bg-[#F1F2F5]"
            }
          />
        </SettingsDropdown>
      </div>
    </div>
  </Card>
);

export const EmailConnectionEmptyState: FC<EmailConnectionEmptyStateProps> = ({
  onConnect,
}) => (
  <Card extraClasses={"overflow-hidden"}>
    <div
      className={
        "flex min-h-[360px] flex-col items-center justify-center px-6 py-12 text-center font-Manrope"
      }>
      <div
        className={
          "mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#F4F2FF] text-[#6B4EFF]"
        }>
        <LuMail size={24} />
      </div>
      <div className={"text-[18px] font-extrabold text-[#1F2433]"}>
        Connect your email
      </div>
      <p className={"mt-2 max-w-[360px] text-sm leading-6 text-[#667085]"}>
        Connect your email account to see all incoming and outgoing messages
        with this client here.
      </p>
      <Button
        type={"button"}
        variant={"primary"}
        size={"sm"}
        autoWidth
        className={"mt-5 h-9 px-4 text-[13px] font-bold"}
        onClick={onConnect}>
        Connect email
      </Button>
    </div>
  </Card>
);

export const EmailProviderModal: FC<EmailProviderModalProps> = ({
  onClose,
  onConnect,
}) => (
  <div
    className={
      "fixed left-0 top-0 z-50 flex h-screen w-screen items-center justify-center bg-black/20"
    }
    onClick={onClose}>
    <div
      className={
        "w-[320px] rounded-xl border border-[#E4E7EC] bg-white p-5 font-Manrope shadow-box"
      }
      onClick={(event) => event.stopPropagation()}>
      <div className={"pb-3 text-[15px] font-extrabold text-[#1F2433]"}>
        Choose your provider
      </div>
      <Button
        type={"button"}
        variant={"ghost"}
        size={"sm"}
        fullWidth
        leftIcon={
          <img
            className={"h-5 w-[25px]"}
            alt={"Gmail"}
            src={"https://mailmeteor.com/logos/assets/PNG/Gmail_Logo_512px.png"}
          />
        }
        className={
          "h-auto justify-start px-2 py-2.5 text-left text-[15px] font-bold text-[#232A38] hover:bg-[#F4F5F7]"
        }
        onClick={() => onConnect("google")}>
        Google
      </Button>
      <Button
        type={"button"}
        variant={"ghost"}
        size={"sm"}
        fullWidth
        leftIcon={
          <img
            className={"h-[25px] w-[25px]"}
            alt={"Outlook"}
            src={
              "https://mailmeteor.com/logos/assets/PNG/Microsoft_Office_Outlook_Logo_512px.png"
            }
          />
        }
        className={
          "h-auto justify-start px-2 py-2.5 text-left text-[15px] font-bold text-[#232A38] hover:bg-[#F4F5F7]"
        }
        onClick={() => onConnect("microsoft")}>
        Microsoft
      </Button>
    </div>
  </div>
);
